/**
 * ============================================================================
 * Iron Meridian - Country Coverage Assertion
 * ============================================================================
 * 
 * COVERAGE VALIDATION SYSTEM
 * 
 * This module ensures that:
 * 1. Every SVG country path has a valid ISO-3 code
 * 2. Every ISO-3 country in the authoritative list is accounted for
 * 3. Every country has a defined render state (owned or neutral)
 * 4. Missing or mismatched data surfaces as clear errors, NOT silent failures
 * 
 * Coverage Status Values:
 * - OK: Country has SVG path, ISO entry, and valid game state
 * - NO_DATA: Country exists but has no game state (greyed out - acceptable)
 * - SVG_ONLY: Country in SVG but not in ISO list (ERROR)
 * - DATA_ONLY: Country in data but missing SVG path (ERROR)
 * - MISSING_ISO: Country code not in authoritative ISO list (ERROR)
 * 
 * This is a core integrity system - treat coverage gaps as bugs, not features.
 */

// Environment detection
const isDevelopment = () => {
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';
  }
  if (typeof window !== 'undefined') {
    return window.location?.hostname === 'localhost' || 
           window.location?.hostname === '127.0.0.1' ||
           window.__IRON_MERIDIAN_DEV__ === true;
  }
  return false;
};

/**
 * Coverage status enum
 */
export const CoverageStatus = {
  OK: 'OK',
  NO_DATA: 'NO_DATA',
  SVG_ONLY: 'SVG_ONLY',
  DATA_ONLY: 'DATA_ONLY',
  MISSING_ISO: 'MISSING_ISO'
};

/**
 * Coverage assertion error for development mode
 */
class CoverageAssertionError extends Error {
  constructor(message, report) {
    super(`[Coverage Assertion] ${message}`);
    this.name = 'CoverageAssertionError';
    this.report = report;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Comprehensive ISO 3166-1 alpha-3 country codes
 * This is the authoritative list for game coverage validation
 */
export const ISO_ALPHA3_CODES = [
  // Africa (54 countries)
  'DZA', 'AGO', 'BEN', 'BWA', 'BFA', 'BDI', 'CPV', 'CMR', 'CAF', 'TCD',
  'COM', 'COG', 'COD', 'DJI', 'EGY', 'GNQ', 'ERI', 'SWZ', 'ETH', 'GAB',
  'GMB', 'GHA', 'GIN', 'GNB', 'CIV', 'KEN', 'LSO', 'LBR', 'LBY', 'MDG',
  'MWI', 'MLI', 'MRT', 'MUS', 'MAR', 'MOZ', 'NAM', 'NER', 'NGA', 'RWA',
  'STP', 'SEN', 'SYC', 'SLE', 'SOM', 'ZAF', 'SSD', 'SDN', 'TZA', 'TGO',
  'TUN', 'UGA', 'ZMB', 'ZWE',
  
  // Americas (35 countries)
  'ATG', 'ARG', 'BHS', 'BRB', 'BLZ', 'BOL', 'BRA', 'CAN', 'CHL', 'COL',
  'CRI', 'CUB', 'DMA', 'DOM', 'ECU', 'SLV', 'GRD', 'GTM', 'GUY', 'HTI',
  'HND', 'JAM', 'MEX', 'NIC', 'PAN', 'PRY', 'PER', 'KNA', 'LCA', 'VCT',
  'SUR', 'TTO', 'USA', 'URY', 'VEN',
  
  // Asia (48 countries)
  'AFG', 'ARM', 'AZE', 'BHR', 'BGD', 'BTN', 'BRN', 'KHM', 'CHN', 'CYP',
  'GEO', 'IND', 'IDN', 'IRN', 'IRQ', 'ISR', 'JPN', 'JOR', 'KAZ', 'KWT',
  'KGZ', 'LAO', 'LBN', 'MYS', 'MDV', 'MNG', 'MMR', 'NPL', 'PRK', 'OMN',
  'PAK', 'PSE', 'PHL', 'QAT', 'SAU', 'SGP', 'KOR', 'LKA', 'SYR', 'TWN',
  'TJK', 'THA', 'TLS', 'TUR', 'TKM', 'ARE', 'UZB', 'VNM', 'YEM',
  
  // Europe (44 countries)
  'ALB', 'AND', 'AUT', 'BLR', 'BEL', 'BIH', 'BGR', 'HRV', 'CZE', 'DNK',
  'EST', 'FIN', 'FRA', 'DEU', 'GRC', 'HUN', 'ISL', 'IRL', 'ITA', 'XKX',
  'LVA', 'LIE', 'LTU', 'LUX', 'MLT', 'MDA', 'MCO', 'MNE', 'NLD', 'MKD',
  'NOR', 'POL', 'PRT', 'ROU', 'RUS', 'SMR', 'SRB', 'SVK', 'SVN', 'ESP',
  'SWE', 'CHE', 'UKR', 'GBR', 'VAT',
  
  // Oceania (14 countries)
  'AUS', 'FJI', 'KIR', 'MHL', 'FSM', 'NRU', 'NZL', 'PLW', 'PNG', 'WSM',
  'SLB', 'TON', 'TUV', 'VUT',
  
  // Special cases / Disputed
  'ESH', // Western Sahara
  'NCL', // New Caledonia (French territory but often shown)
  'GRL', // Greenland
  'PRI', // Puerto Rico
];

// Create a Set for fast lookup
const ISO_CODES_SET = new Set(ISO_ALPHA3_CODES);

/**
 * Validate if a code is in the authoritative ISO list
 * @param {string} code - ISO-3 country code
 * @returns {boolean}
 */
export function isValidIsoCode(code) {
  return ISO_CODES_SET.has(code);
}

/**
 * Assert country coverage and generate detailed report
 * 
 * @param {string[]} svgCountries - Array of country IDs from SVG paths
 * @param {string[]} isoCountries - Authoritative ISO-3 codes (defaults to built-in list)
 * @param {object} gameState - Game state object with country data { countryId: { controller, ... } }
 * @returns {CoverageReport} Detailed coverage report
 */
export function assertCountryCoverage(svgCountries, isoCountries = ISO_ALPHA3_CODES, gameState = {}) {
  const svgSet = new Set(svgCountries);
  const isoSet = new Set(isoCountries);
  const gameStateSet = new Set(Object.keys(gameState));
  
  const report = {
    timestamp: new Date().toISOString(),
    summary: {
      svgCount: svgCountries.length,
      isoCount: isoCountries.length,
      gameStateCount: Object.keys(gameState).length,
      okCount: 0,
      noDataCount: 0,
      errorCount: 0
    },
    countries: [],
    errors: [],
    warnings: []
  };

  // Process all unique country codes from all sources
  const allCodes = new Set([...svgCountries, ...isoCountries, ...Object.keys(gameState)]);
  
  for (const code of allCodes) {
    const inSvg = svgSet.has(code);
    const inIso = isoSet.has(code);
    const inGameState = gameStateSet.has(code);
    const hasController = inGameState && gameState[code]?.controller != null;
    
    let status;
    let isError = false;
    
    if (inSvg && inIso && hasController) {
      // Perfect state: country has SVG, is valid ISO, and has game state
      status = CoverageStatus.OK;
      report.summary.okCount++;
    } else if (inSvg && inIso && !hasController) {
      // Country exists but no active game state - will be rendered as neutral
      status = CoverageStatus.NO_DATA;
      report.summary.noDataCount++;
      report.warnings.push({
        code,
        message: `Country '${code}' has no game state - will render as neutral/greyed out`
      });
    } else if (inSvg && !inIso) {
      // SVG has a country that's not in our ISO list - potential data error
      status = CoverageStatus.SVG_ONLY;
      isError = true;
      report.errors.push({
        code,
        type: 'SVG_ONLY',
        message: `Country '${code}' exists in SVG but not in authoritative ISO list`
      });
    } else if (!inSvg && (inGameState || inIso)) {
      // Data exists but no SVG path - missing geometry
      status = CoverageStatus.DATA_ONLY;
      isError = true;
      report.errors.push({
        code,
        type: 'DATA_ONLY',
        message: `Country '${code}' has data but missing SVG path`
      });
    } else if (inGameState && !inIso) {
      // Game state references unknown country
      status = CoverageStatus.MISSING_ISO;
      isError = true;
      report.errors.push({
        code,
        type: 'MISSING_ISO',
        message: `Game state references '${code}' which is not a valid ISO-3 code`
      });
    } else {
      // Edge case - shouldn't happen with proper iteration
      status = CoverageStatus.NO_DATA;
      report.summary.noDataCount++;
    }
    
    if (isError) {
      report.summary.errorCount++;
    }
    
    report.countries.push({
      code,
      inSvg,
      inIso,
      inGameState,
      hasController,
      status
    });
  }
  
  // Sort countries by status (errors first) then by code
  report.countries.sort((a, b) => {
    const statusOrder = {
      [CoverageStatus.SVG_ONLY]: 0,
      [CoverageStatus.DATA_ONLY]: 1,
      [CoverageStatus.MISSING_ISO]: 2,
      [CoverageStatus.NO_DATA]: 3,
      [CoverageStatus.OK]: 4
    };
    const statusDiff = statusOrder[a.status] - statusOrder[b.status];
    if (statusDiff !== 0) return statusDiff;
    return a.code.localeCompare(b.code);
  });
  
  return report;
}

/**
 * Validate coverage and throw/log based on environment
 * 
 * @param {string[]} svgCountries - Array of country IDs from SVG paths
 * @param {object} gameState - Game state object
 * @param {object} options - Validation options
 * @returns {CoverageReport} Coverage report
 * @throws {CoverageAssertionError} In development mode if errors found
 */
export function validateCoverage(svgCountries, gameState = {}, options = {}) {
  const {
    isoCountries = ISO_ALPHA3_CODES,
    failOnError = isDevelopment(),
    failOnWarning = false,
    silent = false
  } = options;
  
  const report = assertCountryCoverage(svgCountries, isoCountries, gameState);
  
  const hasErrors = report.summary.errorCount > 0;
  const hasWarnings = report.warnings.length > 0;
  
  // Log report if not silent
  if (!silent) {
    if (hasErrors || hasWarnings) {
      console.group('[Country Coverage Report]');
      console.log(`Timestamp: ${report.timestamp}`);
      console.log(`SVG Countries: ${report.summary.svgCount}`);
      console.log(`ISO Countries: ${report.summary.isoCount}`);
      console.log(`Game State Entries: ${report.summary.gameStateCount}`);
      console.log(`OK: ${report.summary.okCount} | No Data: ${report.summary.noDataCount} | Errors: ${report.summary.errorCount}`);
      
      if (hasErrors) {
        console.group('❌ Errors:');
        report.errors.forEach(err => console.error(`  [${err.type}] ${err.code}: ${err.message}`));
        console.groupEnd();
      }
      
      if (hasWarnings) {
        console.group('⚠️ Warnings:');
        report.warnings.forEach(warn => console.warn(`  ${warn.code}: ${warn.message}`));
        console.groupEnd();
      }
      
      console.groupEnd();
    } else {
      console.log(`[Country Coverage] ✅ All ${report.summary.svgCount} countries validated successfully`);
    }
  }
  
  // Fail fast in development
  if (failOnError && hasErrors) {
    throw new CoverageAssertionError(
      `${report.summary.errorCount} coverage error(s) detected`,
      report
    );
  }
  
  if (failOnWarning && hasWarnings) {
    throw new CoverageAssertionError(
      `${report.warnings.length} coverage warning(s) detected`,
      report
    );
  }
  
  return report;
}

/**
 * Get countries that are missing from game state (will render as neutral)
 * @param {string[]} svgCountries - Array of country IDs from SVG paths  
 * @param {object} gameState - Game state object
 * @returns {string[]} Array of country codes without game state
 */
export function getMissingGameStateCountries(svgCountries, gameState = {}) {
  const gameStateSet = new Set(Object.keys(gameState));
  return svgCountries.filter(code => !gameStateSet.has(code));
}

/**
 * Get countries that have game state but no SVG path
 * @param {string[]} svgCountries - Array of country IDs from SVG paths
 * @param {object} gameState - Game state object
 * @returns {string[]} Array of orphaned country codes
 */
export function getOrphanedGameStateCountries(svgCountries, gameState = {}) {
  const svgSet = new Set(svgCountries);
  return Object.keys(gameState).filter(code => !svgSet.has(code));
}

/**
 * Quick validation check - returns boolean without detailed report
 * @param {string[]} svgCountries - Array of country IDs from SVG paths
 * @param {object} gameState - Game state object
 * @returns {boolean} True if no errors (warnings are allowed)
 */
export function isValidCoverage(svgCountries, gameState = {}) {
  const report = assertCountryCoverage(svgCountries, ISO_ALPHA3_CODES, gameState);
  return report.summary.errorCount === 0;
}

// Export for testing
export const _internals = {
  isDevelopment,
  CoverageAssertionError,
  ISO_CODES_SET
};

export default {
  CoverageStatus,
  ISO_ALPHA3_CODES,
  isValidIsoCode,
  assertCountryCoverage,
  validateCoverage,
  getMissingGameStateCountries,
  getOrphanedGameStateCountries,
  isValidCoverage
};
